import { useState, useRef, useEffect, useMemo } from 'react';

// Using lucide-react icons since react-icons isn't available
import { Send, Bot, Zap, Star, Table, Download, X, ChevronDown, ChevronUp, Search } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { apiClient } from '../lib/api/apiClient';

// Table Modal Component

function TableModal({ isOpen, onClose, tableData, agentType }) {
  const [sortConfig, setSortConfig] = useState({ 
    key: null, 
    direction: 'asc' 
  });
  const [searchTerm, setSearchTerm] = useState('');

  const getAgentTheme = () => {
    const themes = {
      "ci-cd": "from-blue-600 via-indigo-600 to-indigo-700",
      "cloud-infrastructure": "from-sky-500 via-blue-600 to-blue-700",
      "code-analysis": "from-indigo-600 via-purple-600 to-purple-700",
      "security-scanning": "from-red-500 via-red-600 to-red-700",
      "container-orchestration": "from-cyan-500 via-blue-600 to-blue-700",
      "performance-monitoring": "from-emerald-500 via-teal-600 to-teal-700",
      "load-testing": "from-amber-500 via-orange-500 to-orange-600",
      "incident-response": "from-rose-500 via-red-600 to-red-700",
    };
    return themes[agentType] || "from-indigo-600 via-purple-600 to-blue-700";
  };

  const filteredAndSortedData = useMemo(() => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) return [];
    
    // First filter the data based on search term
    let filteredData = tableData;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filteredData = tableData.filter(row =>
        Object.values(row).some(value => {
          if (value == null) return false;
          return String(value).toLowerCase().includes(searchLower);
        })
      );
    }
    
    // Then sort the filtered data
    let sortableData = [...filteredData];
    
    // Set default sorting to first column if no sort config exists
    const headers = Object.keys(tableData[0]);
    const sortKey = sortConfig.key || headers[0];
    const sortDirection = sortConfig.key ? sortConfig.direction : 'asc';
    
    sortableData.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;
      
      // Convert to strings for comparison if not already
      const aStr = typeof aVal === 'string' ? aVal : String(aVal);
      const bStr = typeof bVal === 'string' ? bVal : String(bVal);
      
      // Try numeric comparison first
      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // String comparison
      const comparison = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sortableData;
  }, [tableData, sortConfig, searchTerm]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const exportToCSV = () => {
    if (!filteredAndSortedData || filteredAndSortedData.length === 0) return;
    
    const headers = Object.keys(filteredAndSortedData[0]);
    const csvContent = [
      headers.join(','),
      ...filteredAndSortedData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Wrap in quotes if contains comma, quote, or newline
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${agentType}_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSortIcon = (header) => {
    const headers = Object.keys(tableData?.[0] || {});
    const currentSortKey = sortConfig.key || headers[0];
    
    if (currentSortKey !== header) {
      return <ChevronUp className="opacity-30" size={14} />;
    }
    
    const currentDirection = sortConfig.key ? sortConfig.direction : 'asc';
    return currentDirection === 'asc' ? 
      <ChevronUp className="opacity-70" size={14} /> : 
      <ChevronDown className="opacity-70" size={14} />;
  };

  if (!isOpen || !tableData || !Array.isArray(tableData) || tableData.length === 0) return null;

  const headers = Object.keys(tableData[0]);
  const agentTheme = getAgentTheme();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className={`bg-gradient-to-r ${agentTheme} p-4 text-white flex items-center justify-between`}>
          <div className="flex items-center">
            <Table className="mr-2" size={20} />
            <h3 className="text-lg font-semibold">Data Visualization</h3>
            <span className="ml-2 text-sm bg-white/20 px-2 py-1 rounded-full">
              {filteredAndSortedData.length} of {tableData.length} rows
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={exportToCSV}
              className="flex items-center bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-md text-sm transition-colors"
            >
              <Download className="mr-1" size={14} />
              Export CSV
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search across all columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Table Container */}
        <div className="px-4 pb-4 overflow-auto max-h-[calc(90vh-180px)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {headers.map((header) => (
                    <th
                      key={header}
                      onClick={() => handleSort(header)}
                      className="text-left p-3 font-semibold text-gray-700 text-sm uppercase tracking-wider border-r border-gray-200 last:border-r-0 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    >
                      <div className="flex items-center justify-between">
                        <span>{header.replace(/_/g, ' ')}</span>
                        {getSortIcon(header)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedData.length === 0 ? (
                  <tr>
                    <td 
                      colSpan={headers.length} 
                      className="p-8 text-center text-gray-500"
                    >
                      {searchTerm.trim() ? 
                        `No results found for "${searchTerm}"` : 
                        'No data available'
                      }
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                      }`}
                    >
                      {headers.map((header) => (
                        <td
                          key={header}
                          className="p-3 text-sm text-gray-600 border-r border-gray-100 last:border-r-0"
                        >
                          <div className="max-w-xs truncate" title={row[header]}>
                            {row[header] ?? '-'}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}





// Simple Markdown component since ReactMarkdown isn't available
function SimpleMarkdown({ children }) {
  if (typeof children !== 'string') return children;
  
  // Basic markdown parsing for common elements
  let content = children
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>');
  
  return <div dangerouslySetInnerHTML={{ __html: content }} />;
}

export default function AgentChat({ agentType = 'general' }) {
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: `Hello! I'm your ${agentType} assistant. How can I help you today?`,
      timestamp: new Date(),
      table: null
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedTableData, setSelectedTableData] = useState([]);

  function toTitleCase(str) {
    if (!str) return "";
    return str
      .toString()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  
  function formatDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  
  function formatValue(key, value) {
    if (value == null) return "";
  
    // Handle booleans
    if (typeof value === "boolean") {
      return value ? "✅ Yes" : "❌ No";
    }
  
    // Handle numbers (leave as is, but you can add formatting if needed)
    if (typeof value === "number") {
      return value;
    }
  
    // Handle arrays
    if (Array.isArray(value)) {
      return value.map((v) => toTitleCase(v)).join(", ");
    }
  
    // Handle date-like strings
    if (
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("dob") ||
      key.toLowerCase().includes("joining") ||
      key.toLowerCase().includes("appointment") ||
      key.toLowerCase().includes("created") ||
      key.toLowerCase().includes("updated")
    ) {
      return formatDate(value);
    }
  
    // Handle email & lowercase fields
    if (key.toLowerCase().includes("email")) {
      return value.toString().toLowerCase();
    }
  
    // Handle PAN, IFSC, UPPERCASE identifiers
    if (key.toLowerCase().includes("pan") || key.toLowerCase().includes("ifsc")) {
      return value.toString().toUpperCase();
    }
  
    // Otherwise title-case it
    return toTitleCase(value);
  }
  
  // Final mapping:
  const formattedTableData = selectedTableData.map(({ created_at, updated_at, ...rest }) => {
    const formatted = {};
    for (const [key, value] of Object.entries(rest)) {
      formatted[key] = formatValue(key, value);
    }
    return formatted;
  });
  
  
  // Add new refs and states for enhanced functionality
  const messagesEndRef = useRef(null);
  const [isTypingNew, setIsTypingNew] = useState(false);
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Simulate AI "thinking" before responding
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setIsTypingNew(true), 300);
      return () => clearTimeout(timer);
    }
    setIsTypingNew(false);
  }, [loading]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message to chat
    const userMessage = { role: 'user', content: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setLoading(true);
    
    try {
      // const response = await fetch('http://161.97.114.240:8888/api/v1/agent-chat/ask', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({
      //    query: currentInput,
      //    agent_type: agentType,
      //    prev_chat: messages.slice(1).map(msg => ({
      //      role: msg.role,
      //      content: msg.content,
      //     //  timestamp: msg.timestamp,
      //    }))   
      //   }),
      // });
      const data = await apiClient.post('/v1/agent-chat/ask', {
        query: currentInput,
        agent_type: agentType,
        prev_chat: messages.slice(1).map(msg => ({
          role: msg.role,
          content: msg.content,
         }))   
      })

      const aiMessage = { 
        role: 'assistant', 
        content: data.answer, 
        timestamp: new Date(), 
        table: data.table || null 
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error:', error.message);
      const aiMessage = { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error while processing your request. Please try again later.', 
        timestamp: new Date(),
        table: null
      };
      setMessages(prev => [...prev, aiMessage]);
    } finally {
      setLoading(false);
    }
  };

  const openTableModal = (tableData) => {
    setSelectedTableData(tableData);
    setTableModalOpen(true);
  };

  // Get agent-specific theme colors based on agent type
  const getAgentTheme = () => {
    const themes = {
      "ci-cd": "from-blue-600 via-indigo-600 to-indigo-700",
      "cloud-infrastructure": "from-sky-500 via-blue-600 to-blue-700",
      "code-analysis": "from-indigo-600 via-purple-600 to-purple-700",
      "security-scanning": "from-red-500 via-red-600 to-red-700",
      "container-orchestration": "from-cyan-500 via-blue-600 to-blue-700",
      "performance-monitoring": "from-emerald-500 via-teal-600 to-teal-700",
      "load-testing": "from-amber-500 via-orange-500 to-orange-600",
      "incident-response": "from-rose-500 via-red-600 to-red-700",
    };
    
    return themes[agentType] || "from-indigo-600 via-purple-600 to-blue-700";
  };

  const agentTheme = getAgentTheme();

  return (
    <>
      <div className="flex flex-col min-h-[90vh] border-0 rounded-xl overflow-hidden shadow-lg bg-white">
        {/* Enhanced Header with Gradient */}
        <div className={`bg-gradient-to-r ${agentTheme} p-5 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="relative">
                <Bot size={20} className="mr-2" />
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full">
                  <span className="absolute inset-0 w-full h-full bg-green-400 rounded-full animate-ping opacity-75"></span>
                </span>
              </div>
              <div>
                <h3 className="font-semibold flex items-center">
                  {agentType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} 
                  <Zap className="ml-2 text-yellow-300" size={14} />
                </h3>
                <div className="text-xs text-white/80 flex items-center mt-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                  Powered by AI
                </div>
              </div>
            </div>
            <div className="flex items-center bg-white/10 rounded-full px-2 py-0.5 text-xs backdrop-blur-sm">
              <Star className="mr-1 text-yellow-300" size={10} />
              AI Enhanced
            </div>
          </div>
        </div>
        
        {/* Improved Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-50 to-white">
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`mb-4 ${msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 shadow-sm ${
                  msg.role === 'user' 
                    ? `bg-gradient-to-br ${agentTheme} text-white rounded-tr-none` 
                    : 'bg-white border border-gray-100 text-gray-800 rounded-tl-none'
                }`}
              >
                <div className="mb-1">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
                
                {/* Table Visualization Button */}
                {msg.table && Array.isArray(msg.table) && msg.table.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <button
                      onClick={() => openTableModal(msg.table)}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        msg.role === 'user'
                          ? 'bg-white/20 hover:bg-white/30 text-white'
                          : `bg-gradient-to-r ${agentTheme} hover:opacity-90 text-white`
                      }`}
                    >
                      <Table className="mr-1.5" size={12} />
                      View Table Data ({msg.table.length} rows)
                    </button>
                  </div>
                )}
                
                {msg.timestamp && (
                  <div className={`text-xs ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'} mt-1 text-right`}>
                    {msg.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Enhanced Typing Indicator */}
          {loading && (
            <div className="flex justify-start mb-4">
              <div className="bg-white border border-gray-100 text-gray-800 rounded-lg rounded-tl-none shadow-sm p-3 max-w-[80%]">
                <div className="flex items-center">
                  <div className="mr-2">
                    <Bot className="text-gray-400" size={14} />
                  </div>
                  {isTypingNew ? (
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 animate-bounce"></div>
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">Analyzing your request...</div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Enhanced Input Area */}
        <div className="p-3 border-t border-gray-100 bg-white">
          <div className="flex items-center p-1 bg-gray-50 rounded-lg border border-gray-200 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              placeholder="Ask the AI agent..."
              className="flex-1 p-2 bg-transparent border-none focus:outline-none text-sm"
              disabled={loading}
            />
            <button 
              onClick={handleSend}
              className={`p-2 rounded-md ${input.trim() ? `bg-gradient-to-r ${agentTheme}` : 'bg-gray-200'} text-white transition-all`}
              disabled={loading || !input.trim()}
            >
              <Send size={16} />
            </button>
          </div>
          <div className="mt-2 text-center">
            <span className="text-xs text-gray-500">
              AI responses are generated based on your Talent Repo environment
            </span>
          </div>
        </div>
      </div>

      {/* Table Modal */}
      {tableModalOpen && (
        <TableModal
          isOpen={tableModalOpen}
          onClose={() => setTableModalOpen(false)}
          tableData={formattedTableData}
          agentType={agentType}
        />
      )}
    </>
  );
}